"use client";

import React from "react";
import { CircleCheck, ShieldCheck, Wrench, Clock, FileText, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const guarantees = [{
  icon: FileText,
  title: "Documentazione Tecnica",
  description: "Elaborazione DVR e pratiche SCIA con estrema precisione ingegneristica."
}, {
  icon: ShieldCheck,
  title: "Conformità Normativa",
  description: "Garanzia di totale adempimento a tutti gli obblighi del D.Lgs 81/08."
}, {
  icon: Clock,
  title: "Interventi Tempestivi",
  description: "Risposte rapide per la gestione di urgenze e scadenze burocratiche."
}, {
  icon: Wrench,
  title: "Supporto Operativo",
  description: "Assistenza tecnica continua per l'implementazione delle misure di sicurezza."
}];
export default function RichiediUnPreventivoTrasparente() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="3294" id="richiedi-una-consulenza" ref={ref} className="dark relative w-full py-20 bg-background overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {}
          <div className={`space-y-8 transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}>
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-foreground uppercase">
                Pronto a mettere in sicurezza la tua azienda?
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-sans">
                Affidati all'esperienza di Studio MESS per una gestione professionale della sicurezza sul lavoro e della progettazione tecnica. La nostra consulenza garantisce conformità totale e protezione per i tuoi lavoratori.
              </p>
            </div>

            <div className="grid gap-6 mt-8">
              {guarantees.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-4 p-4 rounded-none bg-card/30 border border-border/50 backdrop-blur-sm hover:border-accent/50 transition-colors">
                  <div className="p-2 rounded-none bg-secondary/10 text-secondary shrink-0">
                    <item.icon className="size-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground text-lg mb-1 tracking-tight">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground font-sans">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
                <CircleCheck className="text-accent size-4" />
                <span>Risposta tecnica garantita entro 24 ore lavorative</span>
              </div>
            </div>
          </div>

          {}
          <div className={`transition-all duration-700 delay-300 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
            <div className="bg-card border border-border rounded-none shadow-none p-6 md:p-8 relative overflow-hidden">
              {}
              <div className="absolute top-0 left-0 w-full h-1 bg-accent" />
              
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-foreground tracking-tight">Richiedi una Consulenza</h3>
                <p className="text-muted-foreground text-sm mt-1 font-sans">
                  Compila il modulo per ricevere un'analisi preliminare del tuo caso.
                </p>
              </div>

              <FormProvider formKey="richiedi_consulenza_home" formId="richiedi_consulenza_home" recipientEmail="segreteria@studiomess.it" sectionName="richiedi-consulenza" defaultValues={{
              name: "",
              email: "",
              phone: "",
              vehicle_make: "",
              vehicle_model: "",
              vehicle_year: "",
              service_type: "",
              additional_details: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              vehicle_make: {
                required: true
              },
              vehicle_model: {
                required: true
              },
              vehicle_year: {
                required: true,
                min: 1,
                max: 10000
              },
              service_type: {
                required: true
              }
            }} submitText="Invia Richiesta Consulenza" successMessage="Grazie! La tua richiesta è stata ricevuta. Un nostro ingegnere ti contatterà a breve." errorMessage="Si è verificato un errore tecnico. Ti preghiamo di riprovare o contattarci telefonicamente.">
                <QuoteForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-accent/20 rounded-none flex items-center justify-center mb-2">
          <CircleCheck className="w-8 h-8 text-accent" />
        </div>
        <h4 className="text-xl font-bold text-foreground uppercase tracking-tighter">Richiesta Inviata!</h4>
        <p className="text-muted-foreground max-w-xs mx-auto font-sans">{successMessage}</p>
        <Button variant="outline" className="mt-4 rounded-none border-accent text-accent hover:bg-accent hover:text-accent-foreground" onClick={() => window.location.reload()}>
          Invia un'altra richiesta
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {}
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Nome e Cognome</FormLabel>
              <FormControl>
                <input className="flex h-10 w-full rounded-none border border-input bg-muted/10 px-3 py-2 text-sm text-white ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors hover:bg-muted/20" placeholder="Mario Rossi" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email Aziendale</FormLabel>
              <FormControl>
                <input type="email" className="flex h-10 w-full rounded-none border border-input bg-muted/10 px-3 py-2 text-sm text-white ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors hover:bg-muted/20" placeholder="m.rossi@azienda.it" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Recapito Telefonico</FormLabel>
            <FormControl>
              <input type="tel" className="flex h-10 w-full rounded-none border border-input bg-muted/10 px-3 py-2 text-sm text-white ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors hover:bg-muted/20" placeholder="+39 039 1234567" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {}
        <FormField control={control} name="vehicle_make" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Azienda</FormLabel>
              <FormControl>
                <input className="flex h-10 w-full rounded-none border border-input bg-muted/10 px-3 py-2 text-sm text-white ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors hover:bg-muted/20" placeholder="Ragione Sociale" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="vehicle_model" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Settore</FormLabel>
              <FormControl>
                <input className="flex h-10 w-full rounded-none border border-input bg-muted/10 px-3 py-2 text-sm text-white ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors hover:bg-muted/20" placeholder="Es: Edilizia" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="vehicle_year" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Dipendenti</FormLabel>
              <FormControl>
                <input type="number" className="flex h-10 w-full rounded-none border border-input bg-muted/10 px-3 py-2 text-sm text-white ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors hover:bg-muted/20" placeholder="N°" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="service_type" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Servizio Richiesto</FormLabel>
            <FormControl>
              <select className="flex h-10 w-full rounded-none border border-input bg-muted/10 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors hover:bg-muted/20 appearance-none" {...field}>
                <option value="" disabled>Seleziona la tipologia</option>
                <option value="Sicurezza sul Lavoro">Sicurezza sul Lavoro (D.Lgs 81/08)</option>
                <option value="Incarico RSPP Esterno">Incarico RSPP Esterno</option>
                <option value="Prevenzione Incendi">Prevenzione Incendi / SCIA</option>
                <option value="Sicurezza Cantieri">Sicurezza Cantieri (CSP/CSE)</option>
                <option value="Formazione">Formazione Sicurezza</option>
                <option value="Altro">Altro / Progettazione</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Dettagli della richiesta</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[80px] w-full rounded-none border border-input bg-muted/10 px-3 py-2 text-sm text-white ring-offset-background placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors hover:bg-muted/20 resize-y" placeholder="Descrivi brevemente le tue necessità o i rischi specifici della tua attività..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-none text-sm text-destructive font-mono">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-black py-6 text-lg rounded-none shadow-none uppercase tracking-tighter transition-all">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Elaborazione in corso...
          </> : "Richiedi Consulenza Tecnica"}
      </Button>
      
      <p className="text-[10px] text-center text-muted-foreground mt-4 uppercase tracking-widest font-mono">
        Trattamento dati conforme al GDPR e alla privacy policy dello studio.
      </p>
    </Form>;
}